/**
 * React Hook for Cloudflare Analytics
 * Provides easy integration with React components
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import cloudflareAnalytics, { 
  trackEvent, 
  trackPageView, 
  trackInteraction, 
  trackSpaceEvent, 
  trackBlogEvent, 
  trackToolUsage 
} from '../services/cloudflareAnalyticsService';

/**
 * Hook to automatically track page views on route changes
 */
export function useAnalyticsPageTracking() {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname);
  }, [location.pathname]);
}

/**
 * Hook to provide analytics tracking functions
 */
export function useAnalytics() {
  const trackClick = useCallback((element: string, value?: string | number) => {
    trackInteraction(element, 'click', value);
  }, []);

  const trackSpaceActivity = useCallback((
    eventType: 'launch_view' | 'news_read' | 'event_signup' | 'tool_use' | 'observation_upload',
    data?: Record<string, string | number | boolean | undefined>
  ) => {
    trackSpaceEvent(eventType, data);
  }, []);

  const trackBlogActivity = useCallback((
    action: 'read' | 'share' | 'comment',
    postId?: string,
    postTitle?: string
  ) => {
    trackBlogEvent(action, postId, postTitle);
  }, []);

  const trackTool = useCallback((toolName: string, category?: string) => {
    trackToolUsage(toolName, category);
  }, []);

  const trackCustomEvent = useCallback((eventName: string, properties?: Record<string, string | number | boolean | undefined>) => {
    trackEvent(eventName, properties);
  }, []);

  return {
    trackClick,
    trackSpaceActivity,
    trackBlogActivity,
    trackTool,
    trackCustomEvent,
    trackPageView,
    trackInteraction,
  };
}

/**
 * Hook to initialize analytics on app startup
 */
export function useAnalyticsInit() {
  useEffect(() => {
    // Initialize Zaraz if available
    cloudflareAnalytics.initZaraz();
    
    // Track initial app load
    trackEvent('app_load', {
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    });
  }, []);
}

export default useAnalytics;
